import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AOS from "aos";
import "aos/dist/aos.css";
import "./blogdetail.css";
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import LazyImage from '../components/LazyImage';
import markdownParser from '../utils/markdownParser';
// import convertHashtagsToHeadings from '../utils/hashtagConverter'; // Alternative approach
import fixHashtagHeadings from '../utils/simpleHashtagFix'; // Simple approach
import cleanExcerpt from '../utils/cleanExcerpt'; // Clean markdown from excerpts
import { getBlogBySlug, existingBlogData } from '../data/blogData'; // Import shared data
// import blogService from '../services/blogService';
import blogCache from '../services/blogCache';
// import buildDetection from '../utils/buildDetection';
// import firstImage from '../img/1st.webp';
// import secondImage from '../img/2nd.webp';
// import thirdImage from '../img/3rd.webp';
// import fourthImage from '../img/4th.webp';
// import fifthImage from '../img/5th.webp';
// import sixthImage from '../img/6th.jpg';
// import seventhImage from '../img/7th.png';
// import eighthImage from '../img/8th.png';
// import ninthImage from '../img/9th.webp';
// import twentyEighthImage from '../img/28th.jpg';
// import twentyNinthImage from '../img/29th.avif';
// import thirtiethImage from '../img/30th.webp';
// import thirtyFirstImage from '../img/31st.png';
// import thirtyThirdImage from '../img/33th.jpg';
// import abdul1Image from '../img/Abdul - 1.png';
// import abdul2Image from '../img/Abdul - 2.png';


// Global copy function for code blocks
window.copyCodeToClipboard = function(button) {
    console.log('📋 Copy button clicked');
    
    try {
        const codeBlock = button.closest('.code-block-container') || button.closest('.code-block');
        const codeElement = codeBlock.querySelector('code');
        const code = codeElement.textContent;
        
        navigator.clipboard.writeText(code).then(() => {
            const originalContent = button.innerHTML;
            const originalBackground = button.style.background;
            const originalColor = button.style.color;
            
            button.innerHTML = '✓ Copied!';
            button.style.color = '#48bb78';
            
            setTimeout(() => {
                button.innerHTML = originalContent;
                button.style.background = originalBackground;
                button.style.color = originalColor;
            }, 2000);
        }).catch(() => {
            // Fallback
            const textArea = document.createElement('textarea');
            textArea.value = code;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            const originalContent = button.innerHTML;
            button.innerHTML = '✓ Copied!';
            button.style.color = '#48bb78';
            
            setTimeout(() => {
                button.innerHTML = originalContent;
                button.style.color = '';
            }, 2000);
        });
    } catch (error) {
        console.error('Copy error:', error);
    }
};

// Blog data moved to shared file: client/src/data/blogData.js
// This ensures both Blogs.js and BlogDetail.js use the same data source

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [allBlogs] = useState(existingBlogData); // Use shared data
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Load blog from shared data source (supports both hardcoded and dynamic blogs)
    useEffect(() => {
        console.log('🔍 Looking for blog with slug:', slug);
        setLoading(true);
        
        // First try to find in shared hardcoded data
        let currentBlog = getBlogBySlug(slug);
        
        // If not found in hardcoded data, try to get from cache/API
        if (!currentBlog) {
            console.log('🔄 Blog not found in hardcoded data, checking cache/API...');
            
            // Try to get from blog cache (for dynamic blogs)
            const cachedBlogs = blogCache.getCachedBlogs();
            if (cachedBlogs && cachedBlogs.length > 0) {
                currentBlog = cachedBlogs.find(b => 
                    b.slug && b.slug.toLowerCase() === slug?.toLowerCase()
                );
            }
        }
        
        if (currentBlog) {
            console.log('✅ Blog found:', currentBlog.title);
            setBlog(currentBlog);
        } else {
            console.warn('❌ Blog not found with slug:', slug);
            console.log('Available hardcoded slugs:', existingBlogData.map(b => ({
                title: b.title,
                slug: b.slug
            })));
            setBlog(null);
        }
        
        setLoading(false);
    }, [slug]);

    useEffect(() => {
        AOS.init({
            duration: 1000,
            once: true,
        });
        window.scrollTo(0, 0);
    }, []);

    if (loading) {
        return (
            <div className="blog-detail-page">
                <div className="blog-detail-wrapper">
                    <div className="blog-loading">
                        <div className="loading-spinner">
                            <i className="fas fa-spinner fa-spin"></i>
                        </div>
                        <p>Loading blog content...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!blog && !loading) {
        return (
            <div className="blog-detail-page">
                <SEOHead 
                    title="Blog Not Found - AK Tech"
                    description="The requested blog post could not be found."
                />
                <div className="blog-detail-wrapper">
                    <button onClick={() => navigate('/blogs')} className="back-btn">
                        ← Back to Blogs
                    </button>
                    <div className="blog-not-found">
                        <h1>Blog Not Found</h1>
                        <p>The requested blog post could not be found.</p>
                        <button onClick={() => navigate('/blogs')} className="back-btn">
                            ← Back to Blogs
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Get all blogs except current one for recent blogs, sorted by ID (most recent first)
    const recentBlogs = allBlogs
        .filter(b => b.id !== blog?.id)
        .sort((a, b) => b.id - a.id) // Sort by ID descending (newest first)
        .slice(0, 5);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Navigate to blogs page with search query
            navigate(`/blogs?search=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleShare = (platform) => {
        const url = window.location.href;
        const title = blog.title;

        switch (platform) {
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                break;
            case 'email':
                window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
                break;
            default:
                break;
        }
    };

    // Safety check - don't render if blog is null
    if (!blog) {
        return (
            <div className="blog-detail-page">
                <SEOHead 
                    title="Loading... - AK Tech"
                    description="Loading blog content..."
                />
                <div className="blog-detail-wrapper">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading blog...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="blog-detail-page">
            <SEOHead 
                title={blog.title}
                description={cleanExcerpt(blog.shortDescription || blog.excerpt)}
                image={blog.image}
                author={blog.author}
                type="article"
                keywords={`${blog.category}, ${blog.author}, AK Consultants, Technology, Innovation`}
                robots="noindex, follow"
            />
            <div className="blog-detail-wrapper">
                <button onClick={() => navigate('/blogs')} className="back-btn" data-aos="fade-right">
                    ← Back to Blogs
                </button>

                {/* Hero Section - Full Width at Top */}
                <div className="blog-hero-container" data-aos="fade-up">
                    <div className="blog-hero-left">
                        <span className="blog-detail-category">{blog.category}</span>
                        <h1>{blog.title}</h1>
                        <p className="blog-excerpt">{cleanExcerpt(blog.shortDescription || blog.excerpt)}</p>
                        <div className="blog-hero-meta">
                            <div className="author-info-detail">
                                <div className="author-avatar-large">
                                    <i className="fas fa-user"></i>
                                </div>
                                <div className="author-text">
                                    <span className="blog-detail-author">{blog.author}</span>
                                    <span className="author-role">{blog.authorRole}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="blog-hero-right">
                        <LazyImage
                            src={blog.image}
                            alt={blog.title}
                            onError={(e) => {
                                e.target.src = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&q=80";
                            }}
                        />
                    </div>
                </div>

                {/* Two Column Layout Below Hero */}
                <div className="blog-detail-layout">
                    {/* Main Content - Left Side (Wider) */}
                    <article className="blog-main-content" data-aos="fade-up">
                        {/* Blog Content */}
                        <div
                            className="blog-detail-body"
                            dangerouslySetInnerHTML={{ 
                                __html: (() => {
                                    try {
                                        console.log('🚀 BLOG CONTENT ANALYSIS START');
                                        console.log('📄 Full blog content:', blog?.content || 'No content');
                                        console.log('📏 Content length:', blog?.content?.length || 0);
                                        
                                        // Process blog content
                                        if (!blog?.content) {
                                            return '<p>Content not available.</p>';
                                        }
                                        
                                        // Check if content has hashtag headings
                                        const hasHashtagHeadings = /^#{1,4}\s+.+$/m.test(blog.content);
                                        console.log('🔍 Has hashtag headings (#, ##, ###, ####):', hasHashtagHeadings);
                                        
                                        // Check if content has code blocks
                                        const hasCodeBlocks = blog.content.includes('```');
                                        console.log('🔍 Has code blocks (```):', hasCodeBlocks);
                                        
                                        // Check if content is HTML
                                        const isHTML = blog.content.includes('<h') || blog.content.includes('<p');
                                        console.log('� Is  HTML content:', isHTML);
                                        
                                        // ALWAYS parse if there are hashtag headings, code blocks, or if it's not HTML
                                        if (hasHashtagHeadings || hasCodeBlocks || !isHTML) {
                                            console.log('🔄 PARSING WITH SIMPLE HASHTAG FIX...');
                                            let parsed = fixHashtagHeadings(blog.content);
                                            
                                            // Then apply markdown parser for other features (but skip bold processing)
                                            if (hasCodeBlocks || /^- .+$/m.test(parsed)) {
                                                console.log('🔄 Applying markdown parser for code blocks and bullets...');
                                                parsed = markdownParser.parse(parsed);
                                            }
                                            
                                            console.log('✅ Processing completed');
                                            console.log('📤 Parsed output:', parsed);
                                            return parsed;
                                        } else if (isHTML) {
                                            console.log('✅ Processing HTML content with hashtag fix...');
                                            // ALWAYS apply hashtag fix even for HTML content
                                            let content = fixHashtagHeadings(blog.content);
                                            
                                            // Add copy buttons to code block headers - AFTER the language span
                                            content = content.replace(/<span class="code-language">([^<]*)<\/span>/g, (match) => {
                                                return match + '<button class="copy-code-btn" onclick="window.copyCodeToClipboard(this)"><i class="fas fa-copy"></i> Copy</button>';
                                            });
                                            return content;
                                        }
                                    } catch (error) {
                                        console.error('❌ Error processing blog content:', error);
                                        return '<p>Error loading content. Please try again.</p>';
                                    }
                                })()
                            }}
                        />

                        {/* Display additional images if available */}
                        {blog.images && blog.images.length > 0 && (
                            <div className="blog-image-gallery">
                                <h3>Gallery</h3>
                                <div className="image-gallery-grid">
                                    {blog.images.map((imageUrl, index) => (
                                        <div key={index} className="gallery-image">
                                            <LazyImage
                                                src={imageUrl}
                                                alt={`${blog.title} - Image ${index + 1}`}
                                                onError={(e) => {
                                                    e.target.closest('.gallery-image').style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Share Section */}
                        <div className="blog-share-section">
                            <h3>Share this article</h3>
                            <div className="share-buttons">
                                <button onClick={() => handleShare('linkedin')} className="share-btn linkedin">
                                    <i className="fab fa-linkedin-in"></i>
                                </button>
                                <button onClick={() => handleShare('email')} className="share-btn email">
                                    <i className="fas fa-envelope"></i>
                                </button>
                            </div>
                        </div>
                    </article>

                    {/* Sidebar - Right Side (Narrower) */}
                    <aside className="blog-sidebar" data-aos="fade-left">
                        {/* Search Bar */}
                        <div className="sidebar-widget search-widget">
                            <form onSubmit={handleSearch}>
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Search blogs..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <button type="submit">
                                        <i className="fas fa-search"></i>
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Follow Us */}
                        <div className="sidebar-widget follow-widget">
                            <h3>Follow us on</h3>
                            <div className="social-links">
                                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link linkedin">
                                    <i className="fab fa-linkedin-in"></i>
                                </a>
                                <a href="mailto:info@ak-tech.in" className="social-link email">
                                    <i className="fas fa-envelope"></i>
                                </a>
                            </div>
                        </div>

                        {/* Recent Blogs */}
                        <div className="sidebar-widget recent-blogs-widget">
                            <h3>Recent Blogs</h3>
                            <div className="recent-blogs-list">
                                {recentBlogs.map((recentBlog) => (
                                    <div
                                        key={recentBlog.id}
                                        className="recent-blog-item"
                                        onClick={() => navigate(`/blogs/${recentBlog.slug}`)}
                                    >
                                        <div className="recent-blog-image">
                                            <LazyImage
                                                src={recentBlog.image}
                                                alt={recentBlog.title}
                                                onError={(e) => {
                                                    e.target.src = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&q=80";
                                                }}
                                            />
                                        </div>
                                        <div className="recent-blog-info">
                                            <h4>{recentBlog.title}</h4>
                                            <span className="recent-blog-date">{recentBlog.date}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BlogDetail;